/*
 * Partner landing pages (sections/partner-landing.liquid).
 *
 * - Fills the form's hidden product / UTM fields from the URL. They are also
 *   kept in sessionStorage, because Shopify posts the contact form back to the
 *   page without the original query string.
 * - Fires Meta "Lead" after a successful submit and "Contact" on every
 *   WhatsApp click. Shopify's Meta app sandboxes its pixel, so window.fbq may
 *   not exist; the same events are published to Shopify customer events for a
 *   custom pixel to forward (see docs/partners.md).
 * - Scroll reveal for the blocks and a route on the frames block that draws
 *   itself as you scroll.
 * - Shows the mobile sticky WhatsApp button only while the hero and the form
 *   are both off screen.
 */
(function () {
  var PARAMS = ['product', 'utm_source', 'utm_campaign', 'utm_content'];
  var STORE_KEY = 'tn_partner_params';
  var PENDING_KEY = 'tn_partner_pending';

  function read(key) {
    try {
      return JSON.parse(sessionStorage.getItem(key) || 'null');
    } catch (e) {
      return null;
    }
  }

  function write(key, value) {
    try {
      if (value === null) sessionStorage.removeItem(key);
      else sessionStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* private mode: the URL values still work for this page view */
    }
  }

  function currentParams() {
    var search = new URLSearchParams(location.search);
    var stored = read(STORE_KEY) || {};
    var fromUrl = PARAMS.some(function (name) {
      return search.get(name);
    });
    var params = {};
    PARAMS.forEach(function (name) {
      params[name] = fromUrl ? search.get(name) || '' : stored[name] || '';
    });
    if (fromUrl) write(STORE_KEY, params);
    return params;
  }

  function track(event, metaEvent, data) {
    try {
      if (typeof window.fbq === 'function') {
        window.fbq('track', metaEvent, { content_category: data.segment });
      }
    } catch (e) {
      /* tracking is never worth a broken page */
    }
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(event, data);
      }
    } catch (e) {
      /* as above */
    }
  }

  function init(root) {
    var segment = root.getAttribute('data-segment') || 'hub';
    var params = currentParams();
    var data = Object.assign({ segment: segment }, params);

    root.querySelectorAll('[data-tnp-param]').forEach(function (input) {
      input.value = params[input.getAttribute('data-tnp-param')] || '';
    });

    var interest = root.querySelector('[data-tnp-interest]');
    if (interest && !interest.value) {
      var pick = { frames: 'Frames', patches: 'Patches' }[params.product];
      /* With frames off the choices are ready-made / custom, so there's
         nothing to pre-select. */
      if (pick && interest.querySelector('option[value="' + pick + '"]')) interest.value = pick;
    }

    var formBlock = root.querySelector('[data-tnp-form-block]');
    var form = formBlock && formBlock.querySelector('form');
    if (form) {
      form.addEventListener('submit', function () {
        write(PENDING_KEY, data);
      });
    }

    /* Only a submit made in this tab counts, so a reload of the thank-you URL
       does not report a second lead. */
    var posted = root.querySelector('[data-tnp-posted]');
    var pending = read(PENDING_KEY);
    if (posted && pending) {
      write(PENDING_KEY, null);
      track('partner_lead_submitted', 'Lead', pending);
    }
    if (posted) {
      posted.scrollIntoView({ block: 'center' });
      posted.focus({ preventScroll: true });
    }

    root.querySelectorAll('[data-tnp-wa]').forEach(function (link) {
      link.addEventListener('click', function () {
        track('partner_whatsapp_click', 'Contact', data);
      });
    });

    animate(root);

    root.querySelectorAll('[data-tnp-tabs]').forEach(tabs);

    var sticky = root.querySelector('[data-tnp-sticky]');
    if (sticky && 'IntersectionObserver' in window) {
      var watched = [root.querySelector('[data-tnp-hero]'), formBlock].filter(Boolean);
      var visible = new Set();
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) visible.add(entry.target);
          else visible.delete(entry.target);
        });
        sticky.classList.toggle('is-hidden', visible.size > 0);
      });
      watched.forEach(function (el) {
        observer.observe(el);
      });
      if (!watched.length) sticky.classList.remove('is-hidden');
    } else if (sticky) {
      sticky.classList.remove('is-hidden');
    }
  }

  /* Pricing tabs. The markup shows both panels stacked; this shows the tab
     bar and one panel at a time, with arrow keys moving between tabs. */
  function tabs(box) {
    var list = box.querySelector('[role="tablist"]');
    var buttons = Array.prototype.slice.call(box.querySelectorAll('[role="tab"]'));
    if (!list || buttons.length < 2) return;

    function select(button, focus) {
      buttons.forEach(function (b) {
        var on = b === button;
        b.setAttribute('aria-selected', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
        document.getElementById(b.getAttribute('aria-controls')).hidden = !on;
      });
      if (focus) button.focus();
    }

    buttons.forEach(function (button, i) {
      button.addEventListener('click', function () {
        select(button, false);
      });
      button.addEventListener('keydown', function (event) {
        var step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
        if (!step) return;
        event.preventDefault();
        select(buttons[(i + step + buttons.length) % buttons.length], true);
      });
    });

    list.hidden = false;
    box.classList.add('is-tabbed');
    select(buttons[0], false);
  }

  /* Scroll reveal and the frames route. Without JS, or with reduced motion,
     everything simply shows in its final state. */
  function animate(root) {
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !('IntersectionObserver' in window)) return;
    root.classList.add('tnp-anim');

    var reveal = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          reveal.unobserve(entry.target);
        });
      },
      { threshold: 0.2 }
    );
    root.querySelectorAll('[data-tnp-reveal]').forEach(function (el) {
      reveal.observe(el);
    });

    root.querySelectorAll('[data-tnp-route]').forEach(drawRoute);
  }

  /* The route draws from 0 to 1 as it scrolls from the bottom of the screen
     to about a third of the way down, so scrolling back up rewinds it. */
  function drawRoute(svg) {
    var pins = svg.querySelectorAll('[data-at]');
    var queued = false;

    function update() {
      queued = false;
      var vh = window.innerHeight;
      var top = svg.getBoundingClientRect().top;
      var p = Math.min(1, Math.max(0, (vh * 0.9 - top) / (vh * 0.55)));
      svg.style.setProperty('--p', p.toFixed(3));
      pins.forEach(function (pin) {
        pin.classList.toggle('is-on', p >= parseFloat(pin.getAttribute('data-at')));
      });
    }

    function queue() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue);
    update();
  }

  function start() {
    document.querySelectorAll('[data-tnp]').forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
